import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, ExpandableSection, Link, Popover, SpaceBetween, Spinner, StatusIndicator,
} from '@amzn/awsui-components-react';
import { useLocation, useParams } from 'react-router-dom';
import 'src/assets/styles/steps-wizard.scss';
import ServiceCollection from 'src/services/ServiceCollection';
import { GlobalAppContext } from 'src/components/App';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import DateUtils from 'src/utils/dateUtils';
import ErrorUtils from 'src/utils/ErrorUtils';
import StepsWizardBar from 'src/components/shared/StepsWizardBar';
import WizardStepContentWrapper from 'src/components/calculation-builder/WizardStepContentWrapper';
import { WizardStep } from 'src/models/common/WizardStep';
import { STATUS_INDICATOR_CONFIG } from 'src/components/multidimensional-allocation/common/Constants';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS, derivedAllocationSheetBuilderReducer } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import { deriveDasViewMode, initialState } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderState';

export const DAS_STEP_IDS = ['das-step-1', 'das-step-2', 'das-step-3', 'das-step-4'] as const;

const STEP_NAMES = ['Configure data set', 'Configure totals', 'Configure allocation groups', 'Segment to group mapping'];

const scrollToDasStep = (index: number) => {
    const element = document.getElementById(DAS_STEP_IDS[index]);
    if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

/** Body of a step that is not available yet. */
export function DasStepPlaceholder() {
    return <Box color="text-body-secondary">Coming soon</Box>;
}

function SheetStatus({ status }: { status: string }) {
    const config = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
    if (!config) return <>{status}</>;
    const [label, type, color] = config;
    return <StatusIndicator data-class="statusText" type={type} colorOverride={color}>{label}</StatusIndicator>;
}

/**
 * Submit and review details shown behind the status Info link.
 * @param sheet The sheet whose details are shown.
 * @returns The popover content.
 */
function statusInfoContent(sheet: DerivedAllocationSheetData): React.ReactNode {
    if (!sheet.submitUser) {
        return <span className="statusInfoItem">{sheet.comment || ''}</span>;
    }
    const commenter = sheet.status === 'PENDING_APPROVAL' ? 'Submitter' : 'Reviewer';
    return (
        <ul>
            <li className="statusInfoItem"><b>Submitted by: </b>{sheet.submitUser}</li>
            <li className="statusInfoItem"><b>Submitted on: </b>{sheet.submitDate}</li>
            {!!sheet.reviewUser && <li className="statusInfoItem"><b>Reviewed by: </b>{sheet.reviewUser}</li>}
            {!!sheet.reviewUser && <li className="statusInfoItem"><b>Reviewed on: </b>{sheet.reviewDate}</li>}
            {!!sheet.comment && <li className="statusInfoItem"><b>{commenter} comment: </b>{sheet.comment}</li>}
        </ul>
    );
}

/** Status bar under the steps: the sheet status with its review details, and the last execution time. */
function DasStatusBar({ sheet, canEdit }: { sheet: DerivedAllocationSheetData, canEdit: boolean }) {
    return (
        <>
            <Box padding={{ left: 'n', right: 'l', top: 'xl', bottom: 's' }}>
                <div className="entityStatusBar" data-class="dasStatusBar">
                    <div className="topMarginDiv entityStatusBarElement">
                        <SpaceBetween size="s" direction="horizontal">
                            <strong>Sheet status: </strong>
                            <div>
                                <SheetStatus status={sheet.status} />
                                {(!!sheet.comment || !!sheet.submitUser) && (
                                    <sub>
                                        <Popover dismissButton={false} position="top" triggerType="custom" content={statusInfoContent(sheet)}>
                                            <Link data-class="statusReasonInfoLink" variant="info">Info</Link>
                                        </Popover>
                                    </sub>
                                )}
                            </div>
                        </SpaceBetween>
                    </div>
                    <div className="topMarginDiv verticalSeparator entityStatusBarElement">
                        <SpaceBetween size="s" direction="horizontal">
                            <strong>Last executed:</strong>
                            <div>
                                {sheet.lastExecutionTimestamp
                                    ? DateUtils.formatDate(sheet.lastExecutionTimestamp, 'YYYY/MM/DD hh:mm a')
                                    : <span className="notExecutedText">Not executed</span>}
                            </div>
                        </SpaceBetween>
                    </div>
                </div>
            </Box>
            {canEdit && sheet.status === 'PENDING_APPROVAL' && (
                <div className="subtleAlert">
                    <StatusIndicator data-class="alertText" type="info">This sheet is under review and cannot be modified.</StatusIndicator>
                </div>
            )}
        </>
    );
}

/**
 * Derived allocation sheet builder. On first open it reads the working version (the draft when there is
 * one, else the live version); later refreshes reread the version that was opened. The page title comes
 * from the app header; the page shows the step bar, the status bar and the four step sections. Step 1 opens
 * expanded; selecting a step in the step bar expands it and scrolls to it.
 */
export default function DerivedAllocationSheetBuilder({ services }: { services: ServiceCollection }) {
    const { sheetId } = useParams<{ sheetId: string }>();
    const { pathname } = useLocation();
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);
    const globalDispatch = useContext(GlobalAppContext)?.globalDispatch;
    const [dasState, dasDispatch] = useReducerWithLogger(derivedAllocationSheetBuilderReducer, initialState);
    const { sheet, loading, error, viewMode, sheetRefreshToken } = dasState;
    const openedVersion = useRef<{ sheetId: string, version: number } | null>(null);
    const [selectedStep, setSelectedStep] = useState(1);
    const [expandedSteps, setExpandedSteps] = useState<number[]>([1]);

    useEffect(() => {
        let active = true;
        const opened = openedVersion.current?.sheetId === sheetId ? openedVersion.current.version : null;
        const service = services.derivedAllocationSheetService;
        if (opened == null) {
            // A different sheet is opened: drop the previous one so the spinner shows instead of its page.
            setSelectedStep(1);
            setExpandedSteps([1]);
            if (openedVersion.current != null) {
                openedVersion.current = null;
                dasDispatch(ACTIONS.SET_SHEET_RESULT.withPayload({ sheet: null, availableVersions: [] }));
            }
            dasDispatch(ACTIONS.SET_LOADING.withPayload(true));
        }
        const reportFailure = (message: string) => {
            if (opened == null) {
                dasDispatch(ACTIONS.SET_ERROR.withPayload(message));
            } else {
                services.messageService.showErrorBanner(message);
            }
        };
        (opened == null ? service.getWorkingDerivedAllocationSheet({ sheetId }) : service.getDerivedAllocationSheetAt({ sheetId, version: opened }))
            .then(({ sheetData, availableVersions }) => {
                if (!active) return;
                if (sheetData == null) {
                    reportFailure(`Derived allocation sheet ${sheetId} was not found.`);
                } else {
                    openedVersion.current = { sheetId, version: sheetData.sheetVersion };
                    dasDispatch(ACTIONS.SET_SHEET_RESULT.withPayload({ sheet: sheetData, availableVersions }));
                }
            })
            .catch((ex) => {
                if (active) reportFailure(ErrorUtils.getMessage(ex));
            });
        return () => {
            active = false;
        };
    }, [sheetId, sheetRefreshToken]);

    useEffect(() => {
        dasDispatch(ACTIONS.SET_VIEW_MODE.withPayload(deriveDasViewMode({
            canEdit, status: sheet?.status, sheetVersion: sheet?.sheetVersion,
        })));
    }, [sheet, canEdit]);

    useEffect(() => {
        // Only the sheet the route points at may title the page, so a sheet that is still being replaced does not.
        if (sheet && sheet.sheetId === sheetId && globalDispatch) {
            globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(new DynamicBreadcrumb(pathname, sheet.sheetName, sheet.sheetName)));
        }
    }, [sheet?.sheetId, sheet?.sheetName, sheetId, pathname, globalDispatch]);

    const contextValue = useMemo(
        () => ({ dasState, dasDispatch, services, sheetId }),
        [dasState, dasDispatch, sheetId],
    );

    const wizardSteps: WizardStep[] = STEP_NAMES.map((name, index) => ({
        number: index + 1,
        name,
        scrollToId: DAS_STEP_IDS[index],
        isEnabled: true,
        isSelected: index + 1 === selectedStep,
    }));

    /** Selects a step in the step bar, expands its section and scrolls to it. */
    const goToStep = (number: number) => {
        setSelectedStep(number);
        setExpandedSteps((steps) => (steps.includes(number) ? steps : [...steps, number]));
        scrollToDasStep(number - 1);
    };

    /** Opens or closes one step section. */
    const setStepExpanded = (number: number, expanded: boolean) => {
        setExpandedSteps((steps) => (expanded ? [...steps.filter((n) => n !== number), number] : steps.filter((n) => n !== number)));
    };

    let body: React.ReactNode;
    if (sheet) {
        body = (
            <div className="stepsWizard">
                <SpaceBetween direction="vertical" size="m">
                    <div className="stepsStripContainer">
                        <StepsWizardBar
                            wizardSteps={wizardSteps}
                            selectedWizardStep={wizardSteps[selectedStep - 1]}
                            onStepChanged={(step) => goToStep(step.number)}
                        />
                    </div>
                    <DasStatusBar sheet={sheet} canEdit={canEdit} />
                    {wizardSteps.map((step) => (
                        <WizardStepContentWrapper key={step.scrollToId} wizardStep={step}>
                            <ExpandableSection data-class="polarisExpandableSection" variant="container"
                                               headerText={`Step ${step.number}: ${step.name}`}
                                               expanded={expandedSteps.includes(step.number)}
                                               onChange={({ detail }) => setStepExpanded(step.number, detail.expanded)}>
                                <DasStepPlaceholder />
                            </ExpandableSection>
                        </WizardStepContentWrapper>
                    ))}
                </SpaceBetween>
            </div>
        );
    } else if (loading) {
        body = <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
    } else {
        body = (
            <Alert type="error" header="Unable to load the sheet"
                   action={<Button onClick={() => dasDispatch(ACTIONS.REFRESH_SHEET)}>Retry</Button>}>
                {error}
            </Alert>
        );
    }

    return (
        <DerivedAllocationSheetBuilderContext.Provider value={contextValue}>
            <Box padding={{ top: 'xxl' }}>
                <div className="multidimensionalAllocationBuilder" data-class="dasBuilder" data-view-mode={viewMode}>
                    {body}
                </div>
            </Box>
        </DerivedAllocationSheetBuilderContext.Provider>
    );
}
